import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslate } from '@/translations';
import {
  Alert,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Switch,
  UploadFile,
} from 'antd';
import { Country, Location, LocationType, Select as SelectType } from '@/types';
import { DragUpload } from '@/ui/components';
import { acceptImageFiles, MAX_FILE_SIZE } from '@/utils/constants';

type LocationPopupProps = {
  data?: Location;
  countries: Country[];
  locationTypes: LocationType[];
  customers: SelectType<number>[];
  onSubmit(location: Location): Promise<string | undefined>;
  onCancel(): void;
};

export const LocationPopup: FC<LocationPopupProps> = ({
  data,
  countries,
  locationTypes,
  customers,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslate();
  const [form] = Form.useForm<Location>();

  const [loading, setLoading] = useState(false);
  const [mapImage, setMapImage] = useState<string>();
  const [clearMapImage, setClearMapImage] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const isAdd = data && !data.id;

  useEffect(() => {
    setErrorMessage(undefined);
    setMapImage(data?.mapImage);
    setClearMapImage(false);
    form.resetFields();
    if (data) {
      data.customerIds = data.customers?.map((c) => c.value) || [];
      form.setFieldsValue(data);
    }
  }, [data, form]);

  const handleMapImageChange = (files: UploadFile[]) => {
    if (files.length === 0) {
      setMapImage(undefined);
      setClearMapImage(true);
      return;
    }

    const item = files[0].originFileObj as File;
    if (!item) return;

    const reader = new FileReader();
    reader.onload = ({ target }) => {
      if (target?.result) {
        setMapImage(target.result as string);
        setClearMapImage(false);
      }
    };
    reader.readAsDataURL(item);
  };

  const handleSubmit = useCallback(
    async (value: Location) => {
      setErrorMessage(undefined);
      setLoading(true);
      // Only send base64 when newly uploaded; existing SAS URL left as-is means BE keeps current image.
      value.mapImage = mapImage;
      value.clearMapImage = clearMapImage;
      const error = await onSubmit(value);
      setErrorMessage(error);

      setLoading(false);
      if (!error) onCancel();
    },
    [clearMapImage, mapImage, onCancel, onSubmit],
  );

  return (
    <Modal
      open={!!data}
      onCancel={onCancel}
      title={`${t(isAdd ? 'create' : 'edit')} ${t('location')}`}
      maskClosable
      closable={false}
      okText={t('save')}
      cancelText={t('cancel')}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={680}
      className="mw-100"
      forceRender
    >
      <Spin spinning={loading}>
        <Form
          id="location-form"
          form={form}
          onFinish={handleSubmit}
          size="large"
          labelCol={{ span: 6 }}
          autoComplete="off"
        >
          <Form.Item<Location> name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item<Location>
            label={t('locationDescription')}
            name="desc"
            required
            rules={[{ required: true }]}
          >
            <Input maxLength={100} />
          </Form.Item>

          <Form.Item<Location>
            label={t('capacity')}
            name="capacity"
            required
            rules={[{ required: true }]}
          >
            <Input type="number" />
          </Form.Item>

          <Form.Item<Location>
            label="City"
            name="city"
            required
            rules={[{ required: true }]}
          >
            <Input maxLength={50} />
          </Form.Item>

          <Form.Item<Location> label={t('postCode')} name="postCode">
            <Input maxLength={25} />
          </Form.Item>

          <Form.Item<Location> label="Location Type" name="locationTypeId">
            <Select
              showSearch
              options={locationTypes}
              fieldNames={{ label: 'name', value: 'id' }}
              optionFilterProp="name"
            />
          </Form.Item>

          <Form.Item<Location> label={t('country')} name="countryId">
            <Select
              showSearch
              options={countries.map((o) => ({
                value: o.id,
                label: `${o.name} (${o.iso})`,
              }))}
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item<Location> label="Customers" name="customerIds">
            <Select
              mode="multiple"
              showSearch
              options={customers}
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item<Location> label="Center Lat" name="latitude">
            <InputNumber style={{ width: '100%' }} step={0.000001} />
          </Form.Item>

          <Form.Item<Location> label="Center Lng" name="longitude">
            <InputNumber style={{ width: '100%' }} step={0.000001} />
          </Form.Item>

          <Form.Item<Location> label="Bound North" name="boundNorth">
            <InputNumber style={{ width: '100%' }} step={0.000001} />
          </Form.Item>

          <Form.Item<Location> label="Bound South" name="boundSouth">
            <InputNumber style={{ width: '100%' }} step={0.000001} />
          </Form.Item>

          <Form.Item<Location> label="Bound East" name="boundEast">
            <InputNumber style={{ width: '100%' }} step={0.000001} />
          </Form.Item>

          <Form.Item<Location> label="Bound West" name="boundWest">
            <InputNumber style={{ width: '100%' }} step={0.000001} />
          </Form.Item>

          <Form.Item label="Map Image">
            {mapImage && !mapImage.includes('base64') ? (
              <img
                src={mapImage}
                alt="Map"
                style={{ maxWidth: '100%', maxHeight: 160, marginBottom: 8 }}
              />
            ) : null}
            <DragUpload
              accept={acceptImageFiles}
              uploadButtonText={t('upload')}
              maxFileSize={MAX_FILE_SIZE}
              showUploadBtn={false}
              onFileChanged={handleMapImageChange}
            />
          </Form.Item>

          <Form.Item<Location>
            name="isActive"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          {errorMessage ? (
            <Alert message={errorMessage} type="error" className="mb-4" />
          ) : null}
        </Form>
      </Spin>
    </Modal>
  );
};
